import { type FC, useCallback, useEffect, useRef, useState } from 'react'
import { Image03Icon } from '@/components/ui/icons'
import { ImagePreview } from '@/components/ui/image-preview'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Scrollbar } from '@/components/ui/scrollbar'
import { cn } from '@/helpers/utils'
import { Trans, useTranslation } from '@/translations/i18n'
import type { GalleryImage } from '../types'
import { getGalleryImageUrl } from '../utils'
import { PhotoTourDialog } from './photo-tour-dialog'

interface ScoreFormGalleryProps {
	photos: GalleryImage[]
	listingId: string
	repositoryUrl?: string | null
}

export const ScoreFormGallery: FC<ScoreFormGalleryProps> = ({
	photos,
	listingId,
	repositoryUrl
}) => {
	const { t } = useTranslation()
	const [selectedIndex, setSelectedIndex] = useState(0)
	const [dialogOpen, setDialogOpen] = useState(false)
	const thumbnailRefs = useRef<(HTMLButtonElement | null)[]>([])
	const currentPhoto = photos[selectedIndex]

	const handleKeyDown = useCallback(
		(e: KeyboardEvent) => {
			if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
				e.preventDefault()
				setSelectedIndex((prev) =>
					prev < photos.length - 1 ? prev + 1 : prev
				)
			} else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
				e.preventDefault()
				setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev))
			}
		},
		[photos.length]
	)

	useEffect(() => {
		if (dialogOpen) {
			return
		}

		window.addEventListener('keydown', handleKeyDown)
		return () => window.removeEventListener('keydown', handleKeyDown)
	}, [dialogOpen, handleKeyDown])

	useEffect(() => {
		thumbnailRefs.current[selectedIndex]?.scrollIntoView({
			behavior: 'smooth',
			block: 'nearest'
		})
	}, [selectedIndex])

	return (
		<>
			<div className='mt-6 flex max-h-125 shrink-0 gap-6 overflow-hidden'>
				{/* Main image */}
				<div className='min-w-0 flex-1 border-0.5 border-neutral-black/20'>
					{currentPhoto ? (
						<button
							className='h-full w-full cursor-zoom-in'
							onClick={() => setDialogOpen(true)}
							type='button'
						>
							<ImagePreview
								alt={`Listing ${selectedIndex + 1}`}
								className='h-full w-full rounded-md object-cover'
								height='100%'
								src={getGalleryImageUrl(
									currentPhoto,
									'extraLarge'
								)}
								width='100%'
							/>
						</button>
					) : (
						<div className='col-span-2 flex h-full w-full justify-center bg-neutral-50 text-neutral-400'>
							<div className='flex flex-col items-center py-8'>
								<div className='flex size-19 items-center justify-center rounded-full bg-neutral-100 text-neutral-500'>
									<Image03Icon />
								</div>
								{repositoryUrl ? (
									<>
										<span className='font-medium text-lg text-neutral-900'>
											{t(
												'luxe_submissions:photo_empty_title_repository_url'
											)}
										</span>
										<span className='text-center text-md text-neutral-700'>
											<Trans
												components={{
													a: (
														// biome-ignore lint/a11y/useAnchorContent: use inside Trans component
														<a
															className='cursor-pointer underline'
															href={repositoryUrl}
															rel='noopener noreferrer'
															target='_blank'
														/>
													)
												}}
												i18nKey='luxe_submissions:photo_empty_description_repository_url'
											/>
										</span>
									</>
								) : (
									<>
										{' '}
										<span className='font-medium text-lg text-neutral-900'>
											{t(
												'luxe_submissions:photo_empty_title'
											)}
										</span>
										<span className='text-center text-md text-neutral-700'>
											{t(
												'luxe_submissions:photo_empty_description'
											)}
										</span>
									</>
								)}
							</div>
						</div>
					)}
				</div>

				{/* Thumbnails */}
				{photos.length > 1 && (
					<div className='relative max-h-125 shrink-0'>
						<ScrollArea
							className='h-full'
							scrollbar={<Scrollbar />}
							style={{ overflow: 'hidden' }}
						>
							<div className='flex flex-col pb-6'>
								{photos.map((photo, index) => {
									const correctUrl =
										photo.imageUrl ||
										photo.previewUrls?.extraMedium ||
										undefined
									return (
										<button
											className={cn(
												'mx-1 my-1 h-15 w-20 shrink-0 overflow-hidden rounded-md border border-neutral-black/20 transition-colors',
												index === selectedIndex &&
													'border-2 border-neutral-white shadow-[0_0_0_2px_#000]'
											)}
											key={`thumbnail-${correctUrl}-${index + 1}`}
											onClick={() =>
												setSelectedIndex(index)
											}
											ref={(el) => {
												thumbnailRefs.current[index] =
													el
											}}
											type='button'
										>
											<ImagePreview
												alt={`Thumbnail ${index + 1}`}
												className='aspect-video h-15 w-20 object-cover'
												height='100%'
												src={correctUrl}
												width='100%'
											/>
										</button>
									)
								})}
							</div>
						</ScrollArea>
						<div className='pointer-events-none absolute -bottom-0.5 left-0 h-6 w-full bg-linear-to-b from-transparent to-neutral-white' />
					</div>
				)}
			</div>
			{dialogOpen && (
				<PhotoTourDialog
					initialIndex={selectedIndex}
					listingId={listingId}
					onOpenChange={setDialogOpen}
					open={dialogOpen}
					photos={photos}
				/>
			)}
		</>
	)
}
